/**
 * The ONE shape of "Program access window".
 *
 * Three variants had grown by 2026-08-06: an accent panel on the student
 * create/edit pages, a plain fieldset+legend on the cohort create form,
 * and two bare stacked fields on the cohort edit form — the same concept
 * an operator had to re-recognize on every page, and even the two panels
 * disagreed about their accessible name. The panel is the canonical form
 * because it can say WHERE the dates come from (the badge), which is the
 * thing the live-inheritance model makes confusing.
 *
 * These pieces are LAYOUT ONLY. Each form keeps its own field logic —
 * resolved read-only text, editable inputs, override actions — and
 * renders it into the cells. Behavior stays where the form's state is.
 *
 * Accessible shape, deliberately stable: the region is named
 * "Program access window" (never varying with the duration), and the
 * duration is its own labeled span (`Window length: N days`) so tests
 * and assistive tech can target either without the other.
 */
import { SectionHeader } from '../common/section-header'
import type { ReactNode } from 'react'

export function AccessWindowPanel({
  titleId,
  windowLength,
  badge,
  footer,
  children,
}: {
  /** Page-unique id for the heading; the section is labeled by it. */
  titleId: string
  /** `null` hides the duration; pass "Not fixed" to SAY it is unfixed. */
  windowLength: string | null
  /** Where the dates come from ("Cohort: 2026 wave", "No cohort", …). */
  badge?: ReactNode
  /** Hints and alerts below the grid. */
  footer?: ReactNode
  /** `AccessWindowCell`s. */
  children: ReactNode
}): JSX.Element {
  return (
    <section
      aria-labelledby={titleId}
      className="rounded-token-card border border-border-default border-l-4 border-l-accent bg-surface-app p-3"
    >
      <SectionHeader
        id={titleId}
        title="Program access window"
        meta={<>{windowLength !== null && (
            <span
              className="text-caption text-text-secondary"
              aria-label={`Window length: ${windowLength}`}
            >
              · {windowLength}
            </span>
          )}{badge !== undefined && (
          <span className="rounded bg-accent-soft px-2 py-1 text-label font-medium text-text-primary">
            {badge}
          </span>
        )}</>}
      />
      <dl className="mt-3 grid gap-3 sm:grid-cols-2">{children}</dl>
      {footer}
    </section>
  )
}

export function AccessWindowCell({
  label,
  htmlFor,
  help,
  children,
}: {
  /** "Access starts" / "Access ends" — the pair every surface uses. */
  label: string
  /** Set when the cell holds an input, so the micro-label is a real label. */
  htmlFor?: string
  /** Optional contextual policy help rendered beside the micro-label. */
  help?: ReactNode
  children: ReactNode
}): JSX.Element {
  return (
    <div>
      <dt className="flex items-center gap-1 text-caption font-medium">
        {htmlFor === undefined ? label : <label htmlFor={htmlFor}>{label}</label>}
        {help}
      </dt>
      <dd className="mt-1 text-body font-medium tabular-nums">{children}</dd>
    </div>
  )
}

/** The product's business date is JST, regardless of the operator's
 *  browser — comparing windows against a local "today" would flip answers
 *  nine hours early. */
export function todayJstIso(): string {
  const parts = new Intl.DateTimeFormat('en', {
    timeZone: 'Asia/Tokyo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date())
  const value = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? ''
  return `${value('year')}-${value('month')}-${value('day')}`
}

/** `2026-09-01` → `September 1, 2026`; business dates render in UTC on
 *  purpose — they are calendar days, not instants. */
export function formatBusinessDate(
  value: string | null,
  emptyLabel = '',
): string {
  if (value === null) return emptyLabel
  return new Intl.DateTimeFormat('en', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${value}T00:00:00Z`))
}

/**
 * Inclusive length of the window ("start == end is one program day"),
 * or `null` when it cannot be computed — missing boundary, reversed
 * window, unparseable input. Callers wanting a phrase append their own
 * (`?? 'Not fixed'`).
 */
export function formatWindowLength(
  startsOn: string | null,
  endsOn: string | null,
): string | null {
  if (startsOn === null || startsOn === '' || endsOn === null || endsOn === '') {
    return null
  }
  const start = Date.parse(`${startsOn}T00:00:00Z`)
  const end = Date.parse(`${endsOn}T00:00:00Z`)
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return null
  const days = Math.round((end - start) / 86_400_000) + 1
  return `${days} ${days === 1 ? 'day' : 'days'}`
}
